import { message } from 'antd'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { resolveFoodSafetyNewToday } from 'src/utils/foodSafetyTime'

export const FOOD_SAFETY_NEW_DAY_CHECK_MS = 60 * 1000

export const formatFoodSafetyNewDayNotice = localDate => {
  const m = moment(localDate, 'YYYY-MM-DD', true)
  return m.isValid()
    ? `It's a new day — now showing ${m.format('ddd D MMM')}.`
    : "It's a new day — now showing today."
}

/**
 * A section left open overnight must not save this morning's readings to
 * yesterday. On coming back to the tab (and once a minute, for wall tablets),
 * a screen that was showing today moves to the new day: a `?date=` pin is
 * dropped, otherwise the returned key changes. Screens add the key to their
 * day load and draft reset, the same as a day switch.
 */
export default function useFoodSafetyNewDayReload(day) {
  const history = useHistory()
  const location = useLocation()
  const [newDayKey, setNewDayKey] = useState(0)
  const latest = useRef({ day, history, location })
  latest.current = { day, history, location }
  const firedFor = useRef(null)

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const check = () => {
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      ) {
        return
      }
      const { day: shown, history: nav, location: here } = latest.current
      const next = resolveFoodSafetyNewToday(shown || {})
      if (!next || next === firedFor.current) return
      firedFor.current = next
      message.info(formatFoodSafetyNewDayNotice(next))
      const params = new URLSearchParams(here?.search || '')
      if (params.has('date')) {
        params.delete('date')
        const search = params.toString()
        nav.replace({
          pathname: here.pathname,
          search: search ? `?${search}` : '',
        })
      } else {
        setNewDayKey(key => key + 1)
      }
    }
    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', check)
    const timer = window.setInterval(check, FOOD_SAFETY_NEW_DAY_CHECK_MS)
    return () => {
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', check)
      window.clearInterval(timer)
    }
  }, [])

  return newDayKey
}
